import React, { useEffect, useState } from "react";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../utils/useAuth";
import { createManagerNotification, createCoachNotification } from "../utils/notifications";

export default function ManagerCompetitions() {
  const { user, profile } = useAuth();
  const [competitions, setCompetitions] = useState([]);
  const [newCompetition, setNewCompetition] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [rates, setRates] = useState({
    referee: "",
    assistant1: "",
    assistant2: "",
    assistant4: "",
    assistant5: "",
    timekeeper: "",
    playermovement: "",
    statistician: "",
    technicalzone: "",
    pointoforigin1: "",
    pointoforigin2: "",
  });
  const [competitionRates, setCompetitionRates] = useState({});
  const [loading, setLoading] = useState(true);

  const roles = [
    { key: "referee", label: "Referee" },
    { key: "assistant1", label: "AR1" },
    { key: "assistant2", label: "AR2" },
    { key: "assistant4", label: "A4" },
    { key: "assistant5", label: "A5" },
    { key: "timekeeper", label: "Timekeeper" },
    { key: "playermovement", label: "PMR" },
    { key: "statistician", label: "Statistician" },
    { key: "technicalzone", label: "TZM" },
    { key: "pointoforigin1", label: "PO1" },
    { key: "pointoforigin2", label: "PO2" },
  ];

  // Load competitions
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "competitions"), (snap) => {
      const comps = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort alphabetically
      comps.sort((a, b) => a.name.localeCompare(b.name));
      setCompetitions(comps);
    });

    return unsub;
  }, []);

  // Load billing rates
  useEffect(() => {
    async function loadRates() {
      if (!user) return;
      
      try {
        const ratesDoc = await getDoc(doc(db, "billingRates", user.uid));
        if (ratesDoc.exists()) {
          const data = ratesDoc.data();
          setRates({
            referee: data.referee || "",
            assistant1: data.assistant1 || "",
            assistant2: data.assistant2 || "",
            assistant4: data.assistant4 || "",
            assistant5: data.assistant5 || "",
            timekeeper: data.timekeeper || "",
            playermovement: data.playermovement || "",
            statistician: data.statistician || "",
            technicalzone: data.technicalzone || "",
            pointoforigin1: data.pointoforigin1 || "",
            pointoforigin2: data.pointoforigin2 || "",
          });
          setCompetitionRates(data.competitionRates || {});
        }
      } catch (err) {
        console.error("Error loading rates:", err);
      } finally {
        setLoading(false);
      }
    }
    
    loadRates();
  }, [user]);

  async function addCompetition(e) {
    e.preventDefault();
    if (!newCompetition.trim()) return;

    try {
      await addDoc(collection(db, "competitions"), {
        name: newCompetition.trim(),
        createdBy: profile.fullName,
        createdAt: serverTimestamp(),
      });

      // Notify other managers about new competition
      await createManagerNotification(
        "New Competition Added",
        `${profile.fullName} has created a new competition: ${newCompetition}.`,
        profile.union
      );

      setNewCompetition("");
      alert("Competition added successfully!");
    } catch (err) {
      console.error("Error adding competition:", err);
      alert("Failed to add competition: " + err.message);
    }
  }

  async function saveEdit(id) {
    if (!editingName.trim()) return;

    try {
      await updateDoc(doc(db, "competitions", id), {
        name: editingName.trim(),
        updatedBy: profile.fullName,
        updatedAt: serverTimestamp(),
      });

      setEditingId(null);
      setEditingName("");
      alert("Competition updated!");
    } catch (err) {
      console.error("Error updating competition:", err);
      alert("Failed to update: " + err.message);
    }
  }

  async function deleteCompetition(id, name) {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, "competitions", id));
      alert("Competition deleted!");
    } catch (err) {
      console.error("Error deleting competition:", err);
      alert("Failed to delete: " + err.message);
    }
  }

  async function approveCompetition(id, name) {
    try {
      const comp = competitions.find(c => c.id === id);
      
      await updateDoc(doc(db, "competitions", id), {
        approved: true,
        approvedBy: profile.fullName,
        approvedAt: serverTimestamp(),
      });
      
      // Notify coach who requested it
      if (comp && comp.requestedBy) {
        await createCoachNotification(
          comp.requestedBy,
          "Competition Request Approved",
          `Your competition request "${name}" has been approved and is now available for game requests!`
        );
      }
      
      alert(`Competition "${name}" approved!`);
    } catch (err) {
      console.error("Error approving competition:", err);
      alert("Failed to approve: " + err.message);
    }
  }

  async function rejectCompetition(id, name) {
    const reason = prompt(`Why are you rejecting "${name}"?`);
    if (!reason) return;

    try {
      const comp = competitions.find(c => c.id === id);
      
      await updateDoc(doc(db, "competitions", id), {
        rejected: true,
        rejectionReason: reason,
        rejectedBy: profile.fullName,
        rejectedAt: serverTimestamp(),
      });
      
      // Notify coach who requested it
      if (comp && comp.requestedBy) {
        await createCoachNotification(
          comp.requestedBy,
          "Competition Request Rejected",
          `Your competition request "${name}" was rejected. Reason: ${reason}`
        );
      }
      
      alert(`Competition "${name}" rejected.`);
    } catch (err) {
      console.error("Error rejecting competition:", err);
      alert("Failed to reject: " + err.message);
    }
  }

  function startEdit(comp) {
    setEditingId(comp.id);
    setEditingName(comp.name);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingName("");
  }

  async function saveRates() {
    if (!user) return;

    try {
      await setDoc(doc(db, "billingRates", user.uid), {
        ...rates,
        competitionRates,
        updatedAt: new Date(),
        updatedBy: profile.fullName,
      });
      
      alert("Billing rates saved successfully!");
    } catch (err) {
      console.error("Error saving rates:", err);
      alert("Failed to save rates: " + err.message);
    }
  }

  function updateRate(role, value) {
    setRates({ ...rates, [role]: value });
  }

  function updateCompetitionRate(competition, role, value) {
    setCompetitionRates({
      ...competitionRates,
      [competition]: {
        ...(competitionRates[competition] || {}),
        [role]: value,
      },
    });
  }

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Competitions & Rates</h2>

      {/* SECTION 1: COMPETITION MANAGEMENT */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <h3 className="text-xl font-bold mb-4 border-b pb-2">Competition Management</h3>
        
        {/* Add New Competition */}
        <form onSubmit={addCompetition} className="mb-6">
          <label className="block text-sm font-semibold mb-2">Add New Competition</label>
          <div className="flex gap-3">
            <input
              type="text"
              className="flex-1 p-2 border rounded"
              placeholder="Enter competition name (e.g., Currie Cup, League, etc.)"
              value={newCompetition}
              onChange={(e) => setNewCompetition(e.target.value)}
              required
            />
            <button
              type="submit"
              className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-semibold"
            >
              Add Competition
            </button>
          </div>
        </form>

        {/* PENDING COMPETITION REQUESTS */}
        {competitions.filter(c => c.approved === false && !c.rejected).length > 0 && (
          <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded">
            <h4 className="font-semibold mb-3 text-yellow-800">
              Pending Approval ({competitions.filter(c => c.approved === false && !c.rejected).length})
            </h4>
            <div className="space-y-3">
              {competitions.filter(c => c.approved === false && !c.rejected).map((comp) => (
                <div key={comp.id} className="flex items-center justify-between p-3 bg-white border-l-4 border-yellow-500 rounded">
                  <div>
                    <p className="font-semibold">{comp.name}</p>
                    <p className="text-xs text-gray-600">
                      Requested by: {comp.createdByName || "Unknown"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {comp.createdAt?.toDate().toLocaleDateString()}
                    </p>
                  </div>
                  <div className="space-x-2">
                    <button
                      onClick={() => approveCompetition(comp.id, comp.name)}
                      className="px-4 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => rejectCompetition(comp.id, comp.name)}
                      className="px-4 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Existing Competitions */}
        <div>
          <h4 className="font-semibold mb-3">Existing Competitions ({competitions.filter(c => c.approved !== false).length})</h4>
          {competitions.filter(c => c.approved !== false).length === 0 ? (
            <p className="text-gray-500 text-center py-8">No approved competitions yet.</p>
          ) : (
            <div className="space-y-2">
              {competitions.filter(c => c.approved !== false).map((comp) => (
                <div
                  key={comp.id}
                  className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                >
                  {editingId === comp.id ? (
                    <>
                      <input
                        type="text"
                        className="flex-1 p-2 border rounded"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        autoFocus
                      />
                      <div className="ml-3 space-x-2">
                        <button
                          onClick={() => saveEdit(comp.id)}
                          className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="font-semibold text-lg">{comp.name}</p>
                        <p className="text-xs text-gray-500">
                          {comp.createdByName ? `Requested by ${comp.createdByName}` : `Added by ${comp.createdBy || "Manager"}`}
                        </p>
                      </div>
                      <div className="space-x-2">
                        <button
                          onClick={() => startEdit(comp)}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteCompetition(comp.id, comp.name)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Competitions added here will automatically appear in:
          </p>
          <ul className="list-disc list-inside text-sm text-blue-700 mt-2 ml-2">
            <li>Game request form (coach dropdown)</li>
            <li>Billing rates table below</li>
          </ul>
        </div>
      </div>

      {/* SECTION 2: DEFAULT BILLING RATES */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <h3 className="text-xl font-bold mb-4 border-b pb-2">Default Billing Rates (Fallback)</h3>
        <p className="text-sm text-gray-600 mb-4">
          Set default rates for each role. These will be used if no competition-specific rate is set.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {roles.map((role) => (
            <div key={role.key}>
              <label className="block text-sm font-semibold mb-1">{role.label}</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-600">R</span>
                <input
                  type="number"
                  className="w-full p-2 pl-8 border rounded"
                  placeholder="0"
                  value={rates[role.key] || ""}
                  onChange={(e) => updateRate(role.key, e.target.value)}
                  min="0"
                />
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={saveRates}
          className="mt-6 w-full bg-green-600 text-white p-3 rounded hover:bg-green-700 font-semibold"
        >
          Save All Billing Rates
        </button>
      </div>

      {/* SECTION 3: RATE BILLED PER GAME */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <h3 className="text-xl font-bold mb-4 border-b pb-2">Rate Billed Per Game</h3>
        <p className="text-sm text-gray-600 mb-4">
          Set rates per role for each competition type. These override default rates when the game matches the competition.
        </p>

        {competitions.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Add competitions above to set rates billed per game.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left font-semibold sticky left-0 bg-gray-100 z-10">Competition</th>
                  {roles.map((role) => (
                    <th key={role.key} className="border p-2 text-center font-semibold text-sm">
                      {role.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {competitions.map((comp) => (
                  <tr key={comp.id} className="hover:bg-gray-50">
                    <td className="border p-2 font-semibold sticky left-0 bg-white z-10">{comp.name}</td>
                    {roles.map((role) => (
                      <td key={role.key} className="border p-1">
                        <div className="relative">
                          <span className="absolute left-2 top-1.5 text-gray-500 text-xs">R</span>
                          <input
                            type="number"
                            className="w-full p-1 pl-6 border rounded text-sm"
                            placeholder="-"
                            value={competitionRates[comp.name]?.[role.key] || ""}
                            onChange={(e) => updateCompetitionRate(comp.name, role.key, e.target.value)}
                            min="0"
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button
          onClick={saveRates}
          className="mt-6 w-full bg-green-600 text-white p-3 rounded hover:bg-green-700 font-semibold"
        >
          Save All Billing Rates
        </button>
      </div>
    </div>
  );
}
